// Sign in with Apple in the browser (WEB_PLAN.md §4.4): Apple's JS in popup mode with the console's Services ID
// (grouped with the app's App ID, so the Apple `sub` and the Supabase user are the same as on iPhone). Same nonce
// handling as iOS: Apple signs the SHA-256 into the ID token, Supabase gets the raw value. No key or client secret.
//
// Browsers only open a popup during a click, so the script and the nonce are prepared BEFORE the click
// (`prepareAppleSignIn`) and `signIn()` calls Apple synchronously from the click handler.
// In e2e mode (env.authStubs) there is no Apple: `appleStubSignIn` mints the fake server's token format.

import { AuthFlowError } from './errors';
import { loadScript } from './loadScript';
import { base64Url, makeNonce, randomBytes, type AuthNonce } from './nonce';
import { fakeIdToken, stubAccount } from './stubIdToken';

const APPLE_SRC = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

interface AppleSignInResponse {
  authorization?: { id_token?: string; code?: string; state?: string };
  user?: { email?: string; name?: { firstName?: string; lastName?: string } };
}

interface AppleIDAuth {
  init: (config: {
    clientId: string;
    scope: string;
    redirectURI: string;
    state: string;
    nonce: string;
    usePopup: boolean;
  }) => void;
  signIn: () => Promise<AppleSignInResponse>;
}

declare global {
  interface Window {
    AppleID?: { auth?: AppleIDAuth };
  }
}

export interface AppleResult {
  idToken: string;
  rawNonce: string;
  /** Apple shares the name only the first time (consent is shared with the iOS app, so usually null). */
  name: { first: string; last: string } | null;
}

/** Web wording of iOS's Apple failure text (which talks about the iPhone's iCloud sign-in). */
export const APPLE_FAILED_TEXT = "Sign in with Apple didn't finish. Please try again, or try another option.";
const APPLE_NO_TOKEN_TEXT = "Sign in with Apple didn't return a sign-in token. Please try again.";

/** Apple's popup errors that mean the person closed or declined it. */
const CANCELLED = new Set(['popup_closed_by_user', 'user_cancelled_authorize', 'user_trigger_new_signin_flow']);

export interface AppleSignIn {
  /** Resolves when the script and the next nonce are ready (the button can be enabled). */
  ready: Promise<void>;
  /** Opens Apple's popup; call it directly from the click handler. */
  signIn: () => Promise<AppleResult>;
}

export function prepareAppleSignIn(cfg: { servicesId: string; redirectUri: string }): AppleSignIn {
  let next: { nonce: AuthNonce; state: string } | null = null;
  let failed = false;
  const prepare = (): Promise<void> => {
    failed = false;
    const p = (async () => {
      await loadScript(APPLE_SRC);
      next = { nonce: await makeNonce(), state: base64Url(randomBytes(16)) };
    })();
    // A failed script load (offline, blocked) is retried by the next click.
    p.catch(() => {
      failed = true;
    });
    return p;
  };
  let ready = prepare();

  const signIn = async (): Promise<AppleResult> => {
    const auth = window.AppleID?.auth;
    const prepared = next;
    if (!auth || !prepared) {
      // Not ready yet (slow network, or the script failed to load): get ready, then ask for a second click so the
      // popup isn't blocked.
      if (failed) ready = prepare();
      await ready.catch(() => undefined);
      throw new AuthFlowError('failed', APPLE_FAILED_TEXT);
    }
    next = null;
    auth.init({
      clientId: cfg.servicesId,
      scope: 'name email',
      redirectURI: cfg.redirectUri,
      state: prepared.state,
      nonce: prepared.nonce.sha256,
      usePopup: true,
    });
    const pending = auth.signIn();
    // One nonce per attempt: get the next one ready while Apple's popup is open.
    ready = prepare();
    let response: AppleSignInResponse;
    try {
      response = await pending;
    } catch (e) {
      const code = typeof e === 'object' && e !== null ? (e as { error?: unknown }).error : undefined;
      if (typeof code === 'string' && CANCELLED.has(code)) throw new AuthFlowError('cancelled');
      throw new AuthFlowError('failed', APPLE_FAILED_TEXT);
    }
    const auth2 = response.authorization;
    if (auth2?.state !== undefined && auth2.state !== prepared.state) throw new AuthFlowError('failed', APPLE_FAILED_TEXT);
    const idToken = auth2?.id_token;
    if (!idToken) throw new AuthFlowError('failed', APPLE_NO_TOKEN_TEXT);
    const n = response.user?.name;
    const first = n?.firstName?.trim() ?? '';
    const last = n?.lastName?.trim() ?? '';
    return { idToken, rawNonce: prepared.nonce.raw, name: first || last ? { first, last } : null };
  };

  return {
    get ready() {
      return ready;
    },
    signIn,
  };
}

/** e2e stub: the fake server's Apple token for a test account, with a real nonce pair. */
export async function appleStubSignIn(): Promise<AppleResult> {
  const account = stubAccount('apple');
  const nonce = await makeNonce();
  const first = account.firstName?.trim() ?? '';
  const last = account.lastName?.trim() ?? '';
  return {
    idToken: await fakeIdToken('apple', account, nonce.sha256),
    rawNonce: nonce.raw,
    name: first || last ? { first, last } : null,
  };
}
